import React, { useState, useEffect, useCallback } from 'react';
import { Dialog, DialogTitle, DialogContent, Box, Typography, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, TablePagination, Paper } from '@mui/material';
import { medicalCheckupsService } from '../../services/medicalCheckupsService';
import { MedicalCheckupShortDto } from '../../models/medicalCheckups/medicalCheckupShortDto';
import { MedicalCheckupResult, MedicalCheckupResultUtil } from '../../models/enums/medicalCheckupResult';
import { DateUtils } from '../../helpers/dateUtils';
import { ROUTES } from '../../constants/routes';
import Spinner from '../common/Spinner';

interface Props {
    open: boolean;
    onClose: () => void;
}

export const AlcoholCheckupsModal: React.FC<Props> = ({ open, onClose }) => {
    const [data, setData] = useState<MedicalCheckupShortDto[]>([]);
    const [page, setPage] = useState(0);
    const [dataCount, setDataCount] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(15);
    const [loading, setLoading] = useState(false);

    const loadData = useCallback(async () => {
        setLoading(true);
        try {
            const today = new Date();
            today.setHours(0, 0, 0, 0);
            const todayEnd = new Date(today);
            todayEnd.setDate(todayEnd.getDate() + 1);

            // PermissionService на бэкенде автоматически фильтрует по компаниям медика
            const response = await medicalCheckupsService.getByFilter({
                fromDate: DateUtils.toServerString(today),
                toDate: DateUtils.toServerString(todayEnd),
                medicResult: null,
                alcoholFrom: 0.16,
                page: page,
                pageSize: rowsPerPage,
            });

            setData(response.data.data.items);
            setDataCount(response.data.data.count);
        } catch (_error) {
            console.error('Error loading alcohol checkups:', _error);
        } finally {
            setLoading(false);
        }
    }, [page, rowsPerPage]);

    useEffect(() => {
        if (open) {
            loadData();
        }
    }, [open, loadData]);

    const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLTextAreaElement | HTMLInputElement>) => {
        setRowsPerPage(parseInt(event.target.value, 10));
        setPage(0);
    };

    const renderCheckupResult = (result: MedicalCheckupResult) => {
        const strValue = MedicalCheckupResultUtil.getStringValue(result);
        let color = result === MedicalCheckupResult.Rejected ? '#ec4a71' : '#5bba64';
        let backgroundColor = result === MedicalCheckupResult.Rejected ? '#fce4ea' : '#edfbee';

        return (
            <Box
                display="inline-block"
                color={color}
                sx={{
                    backgroundColor: backgroundColor,
                    borderRadius: "25px",
                    paddingLeft: "15px",
                    paddingRight: "15px"
                }}
            >
                {strValue}
            </Box>
        );
    };

    return (
        <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth>
            <DialogTitle sx={{ textAlign: 'center' }}>
                <Typography fontWeight="bold">История медосмотров</Typography>
            </DialogTitle>
            <DialogContent>
                <Box sx={{ position: 'relative' }}>
                    {loading && <Spinner text="Загрузка истории медосмотров..." />}
                    <TableContainer component={Paper}>
                        <Table>
                            <TableHead>
                                <TableRow>
                                    <TableCell>Дата</TableCell>
                                    <TableCell>ФИО водителя</TableCell>
                                    <TableCell>Компания</TableCell>
                                    <TableCell>Алкоголь</TableCell>
                                    <TableCell>Результат</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {data.map((checkup) => {
                                    const checkupUrl = `${ROUTES.medicalCheckups.idPath.replace(':id', checkup.id.toString())}`;
                                    return (
                                        <TableRow
                                            key={checkup.id}
                                            onClick={() => window.open(checkupUrl, '_blank', 'noopener,noreferrer')}
                                            sx={{
                                                cursor: 'pointer',
                                                '&:hover': {
                                                    backgroundColor: 'rgba(0, 0, 0, 0.04)'
                                                }
                                            }}
                                        >
                                            <TableCell>{DateUtils.formatDate(checkup.date)}</TableCell>
                                            <TableCell>{checkup.fio}</TableCell>
                                            <TableCell>{checkup.company.name}</TableCell>
                                            <TableCell>{checkup.indicators.alcohol?.toFixed(2) ?? '-'}</TableCell>
                                            <TableCell>{renderCheckupResult(checkup.medicResult)}</TableCell>
                                        </TableRow>
                                    );
                                })}
                                {data.length === 0 && !loading && (
                                    <TableRow>
                                        <TableCell colSpan={5} align="center">
                                            <Typography>Нет данных</Typography>
                                        </TableCell>
                                    </TableRow>
                                )}
                            </TableBody>
                        </Table>
                    </TableContainer>
                    <TablePagination
                        component="div"
                        count={dataCount}
                        page={page}
                        onPageChange={(_, newPage) => setPage(newPage)}
                        rowsPerPage={rowsPerPage}
                        onRowsPerPageChange={handleChangeRowsPerPage}
                        rowsPerPageOptions={[15, 30, 50]}
                    />
                </Box>
            </DialogContent>
        </Dialog>
    );
};

